import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';
import {
    applyLottieTransformToPixiContainer,
    getLottieFinalTransformForLayer,
    resetTransformOfPixiContainer,
} from '../../libs/Lottie/applyLottieTransformToPixiContainer';
import { LOTTIE_ORIGINAL_DURATION } from '../../constants';
import { BufferingMode, SeekMode } from '../../enums';
import type { TimelineSettings } from '../../libs/BaseTimeline';
import { ConcurrentTimeline } from '../../libs/ConcurrentTimeline';
import { AnimationData } from '../../libs/Lottie/AnimationData';
import type { ColorMapData } from '../../libs/Lottie/plugins/ColorsPlugin';
import type { Lottie, Renderer } from '../../libs/Lottie/types/lottie';
import {
    assertIsValidLottieDuration,
    assertIsValidTransitionLottie,
} from '../../libs/Lottie/validators';
import type { DestroyOptions } from '../../types';
import { assertIsValidUrl, assertResponseOk } from '../../validators';
import { Config } from '../../video-data/config';
import { createValidator, retry } from '../../libs/retry';
import { ColorMap } from '../asset/ColorMap';
import type { LottieAssetDuration } from '../asset/LottieAsset/LottieAsset';
import { LottieAsset } from '../asset/LottieAsset/LottieAsset';
import type { BaseAssetSettings } from '../asset/BaseAsset';
import { BaseGroup } from '../BaseGroup';
import type { AllProperties, LoadSettings } from '../serializableMixin';
import { getAttributesToSerialize, StringAttribute } from '../attributes';
import { ErrorReporter } from '../../libs/error-reporter';

type TransitionUrl = string | null;

/**
 * Describes a transition setup for our transition manager. We have two setups, an in and an out transition. For each
 * setup we create various assets and timelines which we refer to here by their property names which are on the
 * transition manager.
 */
type TransitionSetup = {
    container: TransitionContainerParent;
    timeline: TransitionTimeline;
    visibility: TransitionVisibility;
    maskAsset: MaskTransitionAsset;
    colorAsset: ColorTransitionAsset;
    pushAsset: PushTransitionAsset;
};

/**
 * Describes the decomposed animation data for a transition setup. When loading Lottie data in our transition manager
 * we decompose the animation data into three parts: mask, color and push parts.
 */
type TransitionPartialSetup = {
    maskPartial: Lottie | null;
    colorPartial: Lottie | null;
    pushPartial: Lottie | null;
};

/**
 * Describes the different time related values for a transition setup. We have a container, transition and start time.
 */
type TransitionTimeSetup = {
    containerDuration: number;
    transitionDuration: number;
    startTime: number;
};

// TODO: we probably want to use the TransitionSetup type here since it's very similar
type ShowTransitionSetup = {
    container: TransitionContainer;
    timeline: ConcurrentTimeline;
    startTime: number;
    maskAsset: LottieAsset;
    colorAsset: LottieAsset;
    pushAsset: LottieAsset;
};

type TransitionTarget = MaskTransitionAsset | ColorTransitionAsset | PushTransitionAsset;
type TransitionContainerParent = 'inTransitionContainer' | 'outTransitionContainer';
type MaskTransitionAsset = 'maskInTransitionAsset' | 'maskOutTransitionAsset';
type ColorTransitionAsset = 'colorInTransitionAsset' | 'colorOutTransitionAsset';
type PushTransitionAsset = 'pushInTransitionAsset' | 'pushOutTransitionAsset';
type TransitionTimeline = 'inTransitionTimeline' | 'outTransitionTimeline';
type TransitionVisibility = 'outTransitionVisible' | 'inTransitionVisible';

class TransitionContainer extends BaseGroup {
    override _classType = 'TransitionContainer';
    #duration = 0;

    override get duration() {
        return this.#duration;
    }

    override set duration(value: number) {
        this.#duration = value;
    }

    override get failedToLoad() {
        return this.getAssets({ deep: false }).some((child) => child.failedToLoad);
    }
}

export class TransitionManager extends BaseGroup {
    override _classType = 'TransitionManager';

    #transitionUrl: TransitionUrl = Config.NONE_TRANSITION_URL;
    #transitionDuration: LottieAssetDuration = Config.NONE_TRANSITION_DURATION;
    #transitionColorMaps: ColorMap[] = [];

    @StringAttribute({ choices: Config.ALL_LOTTIE_RENDERERS })
    accessor renderer: Renderer = Config.LOTTIE_RENDERER_CANVAS;

    #failed: boolean = false;
    #transitionDataLoaded = false;
    #loadingSourceUrl: string | null = null;
    #loadedSourceUrl: string | null = null;

    #inTransitionVisible = true;
    #outTransitionVisible = true;

    /**
     * Default values for extracted transition data.
     */
    #extractedDataDefaultValues = {
        // basic
        transitionDuration: 0,
        overlapDuration: 0,

        // in-transition
        inTransitionDuration: 0,
        inTransitionStartTime: 0,
        pushInTransitionPartial: null,
        maskInTransitionPartial: null,
        colorInTransitionPartial: null,

        // out-transition
        outTransitionDuration: 0,
        outTransitionStartTime: 0,
        pushOutTransitionPartial: null,
        maskOutTransitionPartial: null,
        colorOutTransitionPartial: null,
    };

    /**
     * Data extracted from fetched transition source data.
     */
    extractedData = { ...this.#extractedDataDefaultValues };

    _extractedDataCustomSave() {
        return {
            transitionDuration: this.extractedData.transitionDuration,
            overlapDuration: this.extractedData.overlapDuration,
            inTransitionDuration: this.extractedData.inTransitionDuration,
            inTransitionStartTime: this.extractedData.inTransitionStartTime,
            outTransitionDuration: this.extractedData.outTransitionDuration,
            outTransitionStartTime: this.extractedData.outTransitionStartTime,
        };
    }

    _extractedDataCustomLoad(
        data: ReturnType<typeof TransitionManager.prototype._extractedDataCustomSave>,
    ) {
        this.extractedData.transitionDuration = data.transitionDuration;
        this.extractedData.overlapDuration = data.overlapDuration;
        this.extractedData.inTransitionDuration = data.inTransitionDuration;
        this.extractedData.inTransitionStartTime = data.inTransitionStartTime;
        this.extractedData.outTransitionDuration = data.outTransitionDuration;
        this.extractedData.outTransitionStartTime = data.outTransitionStartTime;
    }

    /**
     * Values, assets and timeline required by in-transition.
     * Note: this is for current scene, not previous scene.
     */
    inTransitionDuration = 0;
    inTransitionStartTime: number = 0;
    pushInTransitionAsset: LottieAsset | undefined = undefined;
    maskInTransitionAsset: LottieAsset | undefined = undefined;
    colorInTransitionAsset: LottieAsset | undefined = undefined;
    inTransitionTimeline: ConcurrentTimeline | undefined = undefined;
    inTransitionContainer: TransitionContainer | undefined = undefined;

    /**
     * Values, assets and timeline required by out-transition.
     * Note: this is for current scene, not previous scene.
     */
    outTransitionDuration = 0;
    outTransitionStartTime: number = 0;
    pushOutTransitionAsset: LottieAsset | undefined = undefined;
    maskOutTransitionAsset: LottieAsset | undefined = undefined;
    colorOutTransitionAsset: LottieAsset | undefined = undefined;
    outTransitionTimeline: ConcurrentTimeline | undefined = undefined;
    outTransitionContainer: TransitionContainer | undefined = undefined;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-useless-constructor
    constructor(container: any, settings: BaseAssetSettings) {
        super(container, settings);
    }

    get renderScene() {
        return this.slide._renderScene;
    }

    get sceneTimeline() {
        return this.slide._timeline;
    }

    get transitionUrl(): TransitionUrl {
        return this.#transitionUrl;
    }

    set transitionUrl(value: TransitionUrl) {
        if (this.#transitionUrl === value) {
            return;
        }
        assertIsValidUrl(value, true);
        // TODO LU-2621: Question: can't we put this in checkAndFix? This is bit of an anti-pattern
        this.#resetTransitionManager();
        this.#transitionUrl = value;
        if (value !== Config.NONE_TRANSITION_URL) {
            this.#transitionDuration = LOTTIE_ORIGINAL_DURATION;
        }
        this.requiresRedraw = true;
    }

    // TODO LU-2621: We added this here because we wanted to add more logic in transitionUrl
    _transitionUrlCustomLoad(value: TransitionUrl, _settings: LoadSettings) {
        this.#transitionUrl = value;
    }

    /**
     * Only for tests.
     */
    get _rawTransitionDuration() {
        return this.#transitionDuration;
    }

    get transitionDuration(): number {
        if (this.#transitionDuration === LOTTIE_ORIGINAL_DURATION) {
            return this.#animationData?.originalDuration || 0;
        }
        return this.#transitionDuration;
    }

    set transitionDuration(value: LottieAssetDuration) {
        if (this.#transitionDuration === value) {
            return;
        }
        this.#assertIsValidTransitionDuration(value);
        this.#transitionDuration = value;
        this.requiresRedraw = true;
    }

    get transitionColorMaps(): ColorMap[] {
        return this.#transitionColorMaps;
    }

    #animationData: AnimationData | null = null;

    get animationData() {
        if (this.#transitionUrl === Config.NONE_TRANSITION_URL) {
            return null;
        }
        if (this.#transitionDuration === Config.NONE_TRANSITION_DURATION) {
            return null;
        }
        return this.#animationData;
    }

    get hasNoneTransition() {
        return this.#transitionUrl === Config.NONE_TRANSITION_URL;
    }

    get hasMaskInTransition() {
        return !!this.maskInTransitionAsset;
    }

    get hasMaskOutTransition() {
        return !!this.maskOutTransitionAsset;
    }

    get hasColorInTransition() {
        return !!this.colorInTransitionAsset;
    }

    get hasColorOutTransition() {
        return !!this.colorOutTransitionAsset;
    }

    get hasInTransition() {
        return !!(this.inTransitionContainer && this.inTransitionTimeline);
    }

    get hasOutTransition() {
        return !!(this.outTransitionContainer && this.outTransitionTimeline);
    }

    get isDoingInTransition() {
        return this.inTransitionTimeline?.active || false;
    }

    get isDoingOutTransition() {
        return this.outTransitionTimeline?.active || false;
    }

    get isDoingTransition() {
        return this.isDoingInTransition || this.isDoingOutTransition;
    }

    get isVisible() {
        return this.inTransitionVisible;
    }

    hideTransition() {
        this.#setTransitionVisible(false);
    }

    showTransition() {
        this.#setTransitionVisible(true);
    }

    #setTransitionVisible(value: boolean) {
        this.inTransitionVisible = value;
        this.#updateTransitionVisibility();
        if (this.slide.prevScene) {
            this.slide.prevScene.customTransition.outTransitionVisible = value;
            this.slide.prevScene.customTransition.#updateTransitionVisibility();
        }
    }

    /**
     * Sets the in-transition visibility
     */
    set inTransitionVisible(value: boolean) {
        this.#inTransitionVisible = value;
        if (!this.inTransitionContainer) {
            return;
        }
        this.inTransitionContainer.visible = value;
    }

    get inTransitionVisible() {
        return this.#inTransitionVisible;
    }

    /**
     * Sets the out-transition visibility
     */
    set outTransitionVisible(value: boolean) {
        this.#outTransitionVisible = value;
        if (!this.outTransitionContainer) {
            return;
        }
        this.outTransitionContainer.visible = value;
    }

    get outTransitionVisible() {
        return this.#outTransitionVisible;
    }
    override get failedToLoad() {
        return this.#failed || this.getAssets({ deep: false }).some((child) => child.failedToLoad);
    }

    override get canPlay(): boolean {
        if (!this._shouldLoad) {
            return true;
        }
        return this.isDoneTryingToLoad;
    }

    override _build() {
        this.#fetchSourceData();
        this.#fitAnimationDataInDuration();
        this.#extractTransitionData();
        if (this.transitionColorMaps.length === 0) {
            this.#initializeColorMaps();
        }
    }

    #fitAnimationDataInDuration() {
        if (!this.animationData) {
            return;
        }
        const duration = this.transitionDuration;
        if (this.animationData.duration === duration) {
            return;
        }
        this.animationData.updateDuration(duration);
    }

    override get _shouldLoad(): boolean {
        if (typeof this.fetchResources !== 'boolean') {
            return this.fetchResources && this.fetchResources.transitions;
        }
        return this.fetchResources;
    }

    get isSourceUrlLoaded(): boolean {
        return this.transitionUrl === this.#loadedSourceUrl;
    }

    #fetchSourceData() {
        if (!this._shouldLoad) {
            return;
        }
        if (this.hasNoneTransition) {
            return;
        }

        // fetch lottie json if not already fetched
        if (!this.#animationData && this.transitionUrl) {
            this.#loadSourceWithNetworkManager();
        }
    }

    get isSourceUrlLoading(): boolean {
        return this.#loadingSourceUrl === this.transitionUrl;
    }

    /**
     * Reports loaded once the lottie renderer is loaded and the underlying Sprite has
     * a valid texture.
     */
    override get isLoaded(): boolean {
        if (this.hasNoneTransition) {
            return true;
        }
        if (!this._shouldLoad) {
            return true;
        }
        return this.#transitionDataLoaded && super.isLoaded;
    }

    override get isDoneTryingToLoad(): boolean {
        return super.isDoneTryingToLoad && (this.isLoaded || this.failedToLoad);
    }

    #loadSourceWithNetworkManager() {
        if (!this._shouldLoad) {
            return;
        }
        if (this.isSourceUrlLoading || this.isSourceUrlLoaded) {
            return;
        }

        this.#failed = false;
        this.#transitionDataLoaded = false;
        this.sentCanPlay = false;

        const url = this.transitionUrl;
        this.#loadingSourceUrl = url;
        this.#loadedSourceUrl = null;
        this.#animationData = null;

        this.root._assetNetworkManager.waitForNetworkSlot(this, (requestFinished) => {
            this.#loadSource(url)
                .catch((e) => {
                    if (url !== this.#loadingSourceUrl) {
                        return; // another fetch has started so ignore the result of this one
                    }
                    console.error(`LottieAsset failed to load source: ${url}, ${e}`);
                    this.#markAsFailed(e);
                })
                .finally(() => {
                    requestFinished();
                    this.requiresRedraw = true;
                });
        });
    }

    async #loadSource(sourceUrl: string) {
        const { json } = await this.#doFetch(sourceUrl);
        if (sourceUrl !== this.#loadingSourceUrl) {
            return; // another fetch has started so ignore the result of this one
        }
        assertIsValidTransitionLottie(json, this.root.aspectRatio);
        this.#animationData = new AnimationData(json);
        this.#loadingSourceUrl = null;
        this.#loadedSourceUrl = sourceUrl;
    }

    async #doFetch(sourceUrl: string): Promise<{ json: Lottie }> {
        const validator = createValidator(assertResponseOk);
        const maxAttempts = this.root?.settings.loadStatusMaxCount || 3;
        const response = await retry(maxAttempts, validator(fetch), sourceUrl);
        const blob = await response.blob();
        const type = blob.type;
        const isJson = type.includes('application/json');
        const isText = type.includes('text/plain');
        if (!(isJson || isText)) {
            throw new Error(`Invalid sourceUrl for lottie asset, detected type: ${type}`);
        }

        const text = await blob.text();
        const json = JSON.parse(text);
        return { json };
    }

    /**
     * Resolves once the data from the transitionUrl has been fetched and validated
     */
    async onLoaded(): Promise<void> {
        if (this.#transitionDataLoaded) {
            return;
        }
        return new Promise<void>((resolve, reject) => {
            this.once('loaded', resolve);
            this.once('loadFailed', reject);
        });
    }

    #markAsFailed(error: Error) {
        this.#failed = true;
        this.emit('loadFailed', error);
        this._emitAssetCanPlay();
    }

    #markAsLoaded() {
        this.#transitionDataLoaded = true;
        this.emit('loaded');
        this._emitAssetCanPlay();
    }

    #extractTransitionData() {
        try {
            if (this.#animationData?.transitionsPlugin) {
                for (const [key, value] of Object.entries(this.#animationData.transitionsPlugin)) {
                    this.extractedData[key] = value;
                }
                this.#markAsLoaded();
            }
        } catch (e) {
            this.#markAsFailed(e);
            ErrorReporter.capture(e);
        }
    }

    #initializeColorMaps() {
        if (!this.animationData) {
            return;
        }

        let colorInTransitionColorMaps: ColorMapData[] = [];
        let colorOutTransitionColorMaps: ColorMapData[] = [];

        if (this.extractedData.colorInTransitionPartial) {
            colorInTransitionColorMaps = this.#extractColorMapsFromPartial(
                this.extractedData.colorInTransitionPartial,
            );
        }
        if (this.extractedData.colorOutTransitionPartial) {
            colorOutTransitionColorMaps = this.#extractColorMapsFromPartial(
                this.extractedData.colorOutTransitionPartial,
            );
        }

        const colorTransitionColorMaps = this.#combineColorTransitionColorMaps(
            colorInTransitionColorMaps,
            colorOutTransitionColorMaps,
        );
        this._transitionColorMapsCustomLoad(colorTransitionColorMaps, {
            skipTemplateAttributes: false,
        });
    }

    #extractColorMapsFromPartial(partial: Lottie) {
        const animationData = new AnimationData(partial);
        return animationData.colorMaps;
    }

    #combineColorTransitionColorMaps(...colorMaps: ColorMapData[][]): ColorMapData[] {
        if (colorMaps.length === 0) {
            return [];
        }
        const combinedColorMaps = ([] as ColorMapData[]).concat(...colorMaps);
        const uniqueColorMaps = uniqWith(combinedColorMaps, isEqual);
        return uniqueColorMaps;
    }

    _replaceMaskTransitionColors(asset: LottieAsset) {
        const colorMaps = asset.animationData.colorMaps;
        // all colors in mask asset should be replaced with #ff0000
        const fixedColorMaps = colorMaps.map((colorMap) => ({
            ...colorMap,
            targetColor: 0xff0000,
        }));
        const curColorMaps = asset._colorMapsCustomSave(true, false, false, false);
        if (!isEqual(curColorMaps, fixedColorMaps)) {
            asset._colorMapsCustomLoad(fixedColorMaps, { skipTemplateAttributes: false });
        }
    }

    async _propagateBuffering(bufferingMode: BufferingMode = BufferingMode.ResolveWhenCanPlay) {
        const bufferingPromises: Promise<void>[] = [];
        if (this.isDoingInTransition) {
            const currentTime = this.inTransitionStartTime + this.inTransitionTimeline.currentTime;
            bufferingPromises.push(
                this.inTransitionContainer._bufferAtTime(currentTime, bufferingMode),
            );
        }
        if (this.isDoingOutTransition) {
            const currentTime =
                this.outTransitionStartTime + this.outTransitionTimeline.currentTime;
            bufferingPromises.push(
                this.outTransitionContainer._bufferAtTime(currentTime, bufferingMode),
            );
        }
        await Promise.all(bufferingPromises);
    }

    override _recursiveSeek(_progress: number) {
        this.#updateTransitionVisibility();
    }

    override updateForRender() {
        const assets = [
            this.pushInTransitionAsset,
            this.maskInTransitionAsset,
            this.colorInTransitionAsset,
            this.pushOutTransitionAsset,
            this.maskOutTransitionAsset,
            this.colorOutTransitionAsset,
        ];
        for (const asset of assets) {
            if (asset) {
                asset.updateForRender();
            }
        }
    }

    #updateTransitionVisibility() {
        const isNotDoingTransition = !this.isDoingTransition;
        const isDoingInTransitionButContainerNotVisible =
            this.isDoingInTransition && !this.inTransitionContainer?.visible;
        const isDoingOutTransitionButContainerNotVisible =
            this.isDoingOutTransition && !this.outTransitionContainer?.visible;
        if (
            isNotDoingTransition ||
            isDoingInTransitionButContainerNotVisible ||
            isDoingOutTransitionButContainerNotVisible
        ) {
            this.#hideTransition();
            return;
        }
        this.#showTransition();
    }

    #hideTransition() {
        this.renderScene.mask = null;
        if (this.colorInTransitionAsset) {
            this.colorInTransitionAsset._pixiContainer.visible = false;
        }
        if (this.colorOutTransitionAsset) {
            this.colorOutTransitionAsset._pixiContainer.visible = false;
        }
        if (this.pushInTransitionAsset || this.pushOutTransitionAsset) {
            resetTransformOfPixiContainer(this.renderScene._pixiContainer);
        }
    }

    #showTransition() {
        if (this.isDoingInTransition) {
            this.#showInTransition();
        }
        if (this.isDoingOutTransition) {
            this.#showOutTransition();
        }
    }

    #showInTransition() {
        this.#showTransitionSetup({
            container: this.inTransitionContainer,
            timeline: this.inTransitionTimeline,
            startTime: this.inTransitionStartTime,
            maskAsset: this.maskInTransitionAsset,
            colorAsset: this.colorInTransitionAsset,
            pushAsset: this.pushInTransitionAsset,
        });
    }

    #showOutTransition() {
        this.#showTransitionSetup({
            container: this.outTransitionContainer,
            timeline: this.outTransitionTimeline,
            startTime: this.outTransitionStartTime,
            maskAsset: this.maskOutTransitionAsset,
            colorAsset: this.colorOutTransitionAsset,
            pushAsset: this.pushOutTransitionAsset,
        });
    }

    #showTransitionSetup(config: ShowTransitionSetup) {
        const { timeline, container, startTime, maskAsset, colorAsset, pushAsset } = config;

        let currentProgress = timeline.progress;
        const currentTransitionDuration = container.duration;
        const currentInTransitionDuration = timeline.duration;
        const currentInTransitionOffsetDuration = startTime;
        const currentTime =
            currentInTransitionOffsetDuration + currentProgress * currentInTransitionDuration;
        currentProgress = currentTime / currentTransitionDuration;

        if (maskAsset) {
            this.renderScene.mask = maskAsset.sprite;
            maskAsset.seek(currentProgress, SeekMode.ResolveWhenCanPlay);
        }
        if (colorAsset) {
            colorAsset._pixiContainer.visible = true;
            colorAsset.seek(currentProgress, SeekMode.ResolveWhenCanPlay);
        }
        if (pushAsset && pushAsset.animationItem) {
            pushAsset._seekAnimationItem(currentProgress);
            const transformation = getLottieFinalTransformForLayer(pushAsset.animationItem);
            applyLottieTransformToPixiContainer(
                this.renderScene._pixiContainer,
                transformation,
                this.root.referenceResolution.width / pushAsset.animationData.width,
            );
        }
    }

    #assertIsValidTransitionDuration(value: LottieAssetDuration) {
        assertIsValidLottieDuration(value);
        if (!this.hasNoneTransition) {
            return;
        }
        if (value === Config.NONE_TRANSITION_DURATION) {
            return;
        }
        throw new Error(
            `transitionDuration cannot be ${value} when transitionUrl is ${Config.NONE_TRANSITION_URL}`,
        );
    }

    #createTransitionTimeline(settings: TimelineSettings = {}) {
        settings = {
            duration: Config.MIN_SCENE_DURATION,
            ...settings,
        };
        const timeline = new ConcurrentTimeline(settings);
        return timeline;
    }

    #addTransitionTimeline(target: TransitionTimeline, name: string) {
        if (!this[target]) {
            this[target] = this.#createTransitionTimeline({ name });
            this.sceneTimeline.add(this[target]);
        }
        return this[target];
    }

    #removeTransitionTimeline(target: TransitionTimeline) {
        if (!this[target]) {
            return;
        }
        this.sceneTimeline.remove(this[target]);
        this[target].destroy();
        this[target] = undefined;
    }

    #createTransitionContainer(transitionManager: TransitionManager, settings: { name: string }) {
        const containerSettings = {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            updateParentOnRedraw: true,
            ...settings,
        };
        const transitionContainer = new TransitionContainer(transitionManager, containerSettings);
        return transitionContainer;
    }

    #addTransitionContainer(
        target: TransitionContainerParent,
        name: string,
        visibility: TransitionVisibility,
    ) {
        if (!this[target]) {
            this[target] = this.#createTransitionContainer(this, { name });
            this[target]._pixiContainer.visible = this[visibility]; // initial state match scene-level flag
            this.insertAsset(this[target]);
        }
        return this[target];
    }

    #removeTransitionContainer(target: TransitionContainerParent) {
        if (!this[target]) {
            return;
        }
        this[target].destroy(true);
        this[target] = undefined;
    }

    #createTransitionLottieAsset(
        transitionContainer: TransitionContainer,
        settings: { name: string },
    ) {
        const containerSettings = {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            updateParentOnRedraw: true,
            renderWhenInvisible: true,
            ...settings,
        };
        const asset = new LottieAsset(transitionContainer, containerSettings);
        asset.renderer = this.renderer;
        asset._pixiContainer.visible = false;
        return asset;
    }

    /**
     * For push transition we need to create web asset because we need to extract information from lottie web internal
     * state which Skottie's API doesn't expose.
     */
    #createTransitionLottieWebAsset(
        transitionContainer: TransitionContainer,
        settings: { name: string },
    ) {
        const containerSettings = {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            updateParentOnRedraw: true,
            renderWhenInvisible: true,
            ...settings,
        };
        const asset = new LottieAsset(transitionContainer, containerSettings);
        asset._pixiContainer.visible = false;
        return asset;
    }

    #removeTransitionAsset(target: TransitionTarget) {
        if (!this[target]) {
            return;
        }
        this[target].destroy();
        this[target] = undefined;
    }

    #removeMaskTransitionAsset(target: MaskTransitionAsset) {
        this.#removeTransitionAsset(target);
        if (!this.slide?.destroyed && this.renderScene) {
            this.renderScene.mask = null;
        }
    }

    #removePushTransitionAsset(target: PushTransitionAsset) {
        this.#removeTransitionAsset(target);
        if (!this.slide?.destroyed && this.renderScene?._pixiContainer) {
            resetTransformOfPixiContainer(this.renderScene._pixiContainer);
        }
    }

    /**
     * Creates a transition asset based on the environment it's running in. For web we need to create a web Lottie asset
     * and in node we create a node Lottie asset (which is a wrapper around Skottie).
     */
    #addTransitionAsset(target: TransitionTarget, parent: TransitionContainerParent, name: string) {
        if (!this[target]) {
            this[target] = this.#createTransitionLottieAsset(this[parent], { name });
            this[parent].insertAsset(this[target]);
        }
        return this[target];
    }

    /**
     * Creates a web Lottie asset not matter the environment. This is used for push transitions because we need to
     * extract transformation information for the animation.
     */
    #addWebTransitionAsset(
        target: TransitionTarget,
        parent: TransitionContainerParent,
        name: string,
    ) {
        if (!this[target]) {
            this[target] = this.#createTransitionLottieWebAsset(this[parent], { name });
            this[parent].insertAsset(this[target]);
        }
        return this[target];
    }

    removeInTransition() {
        this.#removeMaskTransitionAsset('maskInTransitionAsset');
        this.#removeTransitionAsset('colorInTransitionAsset');
        this.#removePushTransitionAsset('pushInTransitionAsset');
        this.#removeTransitionContainer('inTransitionContainer');
        this.#removeTransitionTimeline('inTransitionTimeline');
    }

    removeOutTransition() {
        this.#removeMaskTransitionAsset('maskOutTransitionAsset');
        this.#removeTransitionAsset('colorOutTransitionAsset');
        this.#removePushTransitionAsset('pushOutTransitionAsset');
        this.#removeTransitionContainer('outTransitionContainer');
        this.#removeTransitionTimeline('outTransitionTimeline');
    }

    override clear() {
        this.removeInTransition();
        this.removeOutTransition();
        this.#resetTransitionManager();
    }

    #resetTransitionManager() {
        this.#transitionUrl = Config.NONE_TRANSITION_URL;
        this.#transitionDuration = Config.NONE_TRANSITION_DURATION;
        this.extractedData = { ...this.#extractedDataDefaultValues };
        this.#transitionColorMaps = [];
        this.#animationData = null;
        this.#loadingSourceUrl = null;
        this.#loadedSourceUrl = null;
        this.#transitionDataLoaded = false;
    }

    _transitionColorMapsCustomLoad(values: ColorMapData[], settings: LoadSettings) {
        this.#transitionColorMaps = values.map((value) => {
            const colorMap = new ColorMap({ parent: this, redrawParentOnUpdate: true });
            colorMap.load(value, settings);
            return colorMap;
        });
    }

    /**
     * @internal
     */
    _transitionColorMapsCustomSave(
        allProperties: AllProperties,
        clone: boolean,
        returnTemplateReference: boolean,
        skipTemplateAttributes: boolean,
    ) {
        return this.#transitionColorMaps.map((colorMap) =>
            colorMap.save(allProperties, clone, returnTemplateReference, skipTemplateAttributes),
        ) as ColorMapData[];
    }

    /**
     * @internal
     */
    _applyInTransition(
        {
            maskInTransitionPartial,
            colorInTransitionPartial,
            pushInTransitionPartial,
        }: {
            maskInTransitionPartial: Lottie | null;
            colorInTransitionPartial: Lottie | null;
            pushInTransitionPartial: Lottie | null;
        },
        transitionDuration: number,
        inTransitionDuration: number,
        colorMaps: ColorMapData[],
    ) {
        const partials: TransitionPartialSetup = {
            maskPartial: maskInTransitionPartial,
            colorPartial: colorInTransitionPartial,
            pushPartial: pushInTransitionPartial,
        };
        const config: TransitionSetup = {
            container: 'inTransitionContainer',
            timeline: 'inTransitionTimeline',
            visibility: 'inTransitionVisible',
            maskAsset: 'maskInTransitionAsset',
            colorAsset: 'colorInTransitionAsset',
            pushAsset: 'pushInTransitionAsset',
        };
        const time: TransitionTimeSetup = {
            containerDuration: transitionDuration,
            transitionDuration: inTransitionDuration,
            startTime: 0,
        };
        this.#applyTransition(partials, config, time, colorMaps);
    }

    /**
     * @internal
     */
    _applyOutTransition(
        {
            maskOutTransitionPartial,
            colorOutTransitionPartial,
            pushOutTransitionPartial,
        }: {
            maskOutTransitionPartial: Lottie | null;
            colorOutTransitionPartial: Lottie | null;
            pushOutTransitionPartial: Lottie | null;
        },
        transitionDuration: number,
        outTransitionDuration: number,
        colorMaps: ColorMapData[],
    ) {
        const partials: TransitionPartialSetup = {
            maskPartial: maskOutTransitionPartial,
            colorPartial: colorOutTransitionPartial,
            pushPartial: pushOutTransitionPartial,
        };
        const config: TransitionSetup = {
            container: 'outTransitionContainer',
            timeline: 'outTransitionTimeline',
            visibility: 'outTransitionVisible',
            maskAsset: 'maskOutTransitionAsset',
            colorAsset: 'colorOutTransitionAsset',
            pushAsset: 'pushOutTransitionAsset',
        };
        const time: TransitionTimeSetup = {
            containerDuration: transitionDuration,
            transitionDuration: outTransitionDuration,
            startTime: this.slide.duration - outTransitionDuration,
        };
        this.#applyTransition(partials, config, time, colorMaps);
    }

    #applyTransition(
        partials: TransitionPartialSetup,
        config: TransitionSetup,
        time: TransitionTimeSetup,
        colorMaps: ColorMapData[],
    ) {
        if (partials.maskPartial || partials.colorPartial || partials.pushPartial) {
            const index = this.slide.globalIndex;
            this.#addTransitionContainer(
                config.container,
                `${config.container}_${index}`,
                config.visibility,
            );
            this.#addTransitionTimeline(config.timeline, `${config.timeline}_${index}`);
            this[config.container].duration = time.containerDuration;
            this[config.timeline].duration = time.transitionDuration;
            this[config.timeline].relativeStartTime = time.startTime;

            if (partials.maskPartial) {
                const asset = this.#addTransitionAsset(
                    config.maskAsset,
                    config.container,
                    `${config.maskAsset}_${index}`,
                );
                asset.lottieData = partials.maskPartial;
                asset.renderer = this.renderer;
                this._replaceMaskTransitionColors(asset);
                asset.duration = time.containerDuration;
            } else {
                this.#removeMaskTransitionAsset(config.maskAsset);
            }

            if (partials.colorPartial) {
                const asset = this.#addTransitionAsset(
                    config.colorAsset,
                    config.container,
                    `${config.colorAsset}_${index}`,
                );
                asset.lottieData = partials.colorPartial;
                asset.renderer = this.renderer;

                // only set the colorMap when it's actually different, otherwise we'll trigger an update
                const curColorMaps = asset._colorMapsCustomSave(true, false, false, false);
                if (!isEqual(curColorMaps, colorMaps)) {
                    asset._colorMapsCustomLoad(colorMaps, { skipTemplateAttributes: false });
                }
                asset.duration = time.containerDuration;
            } else {
                this.#removeTransitionAsset(config.colorAsset);
            }

            if (partials.pushPartial) {
                const asset = this.#addWebTransitionAsset(
                    config.pushAsset,
                    config.container,
                    `${config.pushAsset}_${index}`,
                );
                asset.lottieData = partials.pushPartial;
                asset.duration = time.containerDuration;
            } else {
                this.#removePushTransitionAsset(config.pushAsset);
            }
        } else {
            this.#removeMaskTransitionAsset(config.maskAsset);
            this.#removeTransitionAsset(config.colorAsset);
            this.#removePushTransitionAsset(config.pushAsset);
            this.#removeTransitionContainer(config.container);
            this.#removeTransitionTimeline(config.timeline);
        }
    }

    override destroy(options?: DestroyOptions) {
        this.clear(); // clear all transition manager resources
        super.destroy(options);
    }

    override get readonlyProperties() {
        return [];
    }

    override get editableProperties() {
        return [];
    }

    override get propertiesToSerialize() {
        return [
            'transitionUrl',
            'transitionDuration',
            'transitionColorMaps',
            'extractedData',
        ].concat(getAttributesToSerialize(this));
    }
}
